/**
 * Field Pattern Recognition Utility
 * 
 * This utility provides functions to identify field types based on patterns
 * rather than hardcoded field names. It supports dynamic field recognition
 * for different contexts in the ticket creation workflow.
 */

/**
 * Patterns used to identify document fields
 * These patterns are used to match field names that likely represent document uploads
 */
export const DOCUMENT_FIELD_PATTERNS = [
  'file',
  'document',
  'certificate',
  'upload',
  'photo',
  'signature',
  'copy',
  'scan',
  'passport',
  'id',
  'contract',
  'visa',
  'shinasnama',
  'labour'
];

// Specific document field names that should always be treated as documents
export const SPECIFIC_DOCUMENT_FIELDS = [
  'birthCertificate',
  'passportCopy',
  'visaPage',
  'salaryCertificate',
  'photo',
  'shinasnama',
  'labourContract',
  'resume',
  'policeClearanceCertificate',
  'educationalCertificate',
  'experienceCertificate'
];

/**
 * Patterns used to identify personal information fields
 */
export const PERSONAL_INFO_FIELD_PATTERNS = [
  'name',
  'firstname',
  'lastname',
  'middlename',
  'gender',
  'birth',
  'dob',
  'age',
  'nationality'
];

/**
 * Patterns used to identify contact information fields
 */
export const CONTACT_INFO_FIELD_PATTERNS = [
  'email',
  'phone',
  'mobile',
  'contact'
];

/**
 * Patterns used to identify address fields
 */
export const ADDRESS_FIELD_PATTERNS = [
  'address',
  'city',
  'state',
  'country',
  'province',
  'zipcode',
  'postalcode'
];

/**
 * Field categories enum
 */
export const FIELD_CATEGORIES = {
  PERSONAL_INFO: 'PERSONAL_INFO',
  CONTACT_INFO: 'CONTACT_INFO',
  ADDRESS: 'ADDRESS',
  DOCUMENTS: 'DOCUMENTS',
  SERVICE_SPECIFIC: 'SERVICE_SPECIFIC'
};

/**
 * Determines if a field is a document field based on its name or properties
 * @param {Object|String} field - Field object or field name
 * @returns {Boolean} - True if the field is a document field
 */
export const isDocumentField = (field) => {
  // Handle both field objects and field names
  const fieldName = typeof field === 'string' ? field : (field.name || field.title || '');
  
  if (!fieldName) return false;
  
  // First check if it's in our specific document fields list
  if (SPECIFIC_DOCUMENT_FIELDS.includes(fieldName)) {
    return true;
  }
  
  // Then check field name against document patterns
  const lowerFieldName = fieldName.toLowerCase();
  return DOCUMENT_FIELD_PATTERNS.some(pattern => lowerFieldName.includes(pattern));
};

/**
 * Determines the category of a field based on its name or properties
 * @param {Object|String} field - Field object or field name
 * @returns {String} - Category identifier from FIELD_CATEGORIES
 */
export const getFieldCategory = (field) => {
  // Handle both field objects and field names
  const fieldName = typeof field === 'string' ? field : (field.name || field.title || '');
  
  if (!fieldName) return FIELD_CATEGORIES.SERVICE_SPECIFIC;
  
  const lowerFieldName = fieldName.toLowerCase();
  
  // Check for document fields first
  if (DOCUMENT_FIELD_PATTERNS.some(pattern => lowerFieldName.includes(pattern))) {
    return FIELD_CATEGORIES.DOCUMENTS;
  }
  
  // Check for personal info fields
  if (PERSONAL_INFO_FIELD_PATTERNS.some(pattern => lowerFieldName.includes(pattern))) {
    return FIELD_CATEGORIES.PERSONAL_INFO;
  }
  
  // Check for contact info fields
  if (CONTACT_INFO_FIELD_PATTERNS.some(pattern => lowerFieldName.includes(pattern))) {
    return FIELD_CATEGORIES.CONTACT_INFO;
  }
  
  // Check for address fields
  if (ADDRESS_FIELD_PATTERNS.some(pattern => lowerFieldName.includes(pattern))) {
    return FIELD_CATEGORIES.ADDRESS;
  }
  
  // Default to service specific if no pattern matches
  return FIELD_CATEGORIES.SERVICE_SPECIFIC;
};

/**
 * Determines field type based on field name or properties
 * @param {Object|String} field - Field object or field name
 * @returns {String} - Field type (FILE, TEXT, DATE, SELECT, etc.)
 */
export const getFieldType = (field) => {
  // If field is an object with a type property, use that
  if (typeof field !== 'string' && field.type) {
    return field.type;
  }
  
  // Handle field name
  const fieldName = typeof field === 'string' ? field : (field.name || field.title || '');
  const lowerFieldName = fieldName.toLowerCase();
  
  // Check if it's a document field
  if (isDocumentField(fieldName)) {
    return 'FILE';
  }
  
  // Check for date fields
  if (lowerFieldName.includes('date') || 
      lowerFieldName.includes('dob') || 
      lowerFieldName.includes('birth')) {
    return 'DATE';
  }
  
  // Check for select fields
  if (lowerFieldName.includes('gender') || 
      lowerFieldName.includes('status') || 
      lowerFieldName.includes('type')) {
    return 'SELECT';
  }
  
  // Default to text
  return 'TEXT';
};
